/**
 * Multi-Document Manifest Tool (FH-CLASSIFIER-002)
 *
 * First-class workflow for batched document intake. When the user uploads
 * 2+ files in a single turn (or asks to "read/process/review these documents"),
 * this tool:
 *
 *   1. Extracts text from every file using the existing client-side helpers
 *      (pdf.js, mammoth, SheetJS, JSZip, plain-text reader, RTF stripper)
 *      and falls back to targeted vision/OCR for scanned PDFs and images.
 *   2. Classifies each doc (type, legal area, language, dates, parties).
 *   3. Summarizes all docs in ONE batched LLM call (OpenRouter → Gemini 3.1
 *      Pro primary → GPT 5.4 secondary per Miles policy in MEMORY.md), with
 *      summaries returned in the SAME language as each source document.
 *   4. Persists the manifest on `sharedContext.documentManifest` so later
 *      turns can reference docs by index ("use #15 for the legal doc").
 *   5. Renders a numbered manifest overview to the user.
 *
 * Max 20 files per call. Each file's extracted text is capped at
 * `MAX_EXTRACT_CHARS` to avoid context blowups; truncation is flagged in
 * the manifest entry so downstream tools know to re-extract if they need
 * full content.
 */

import {
  extractTextFromDocx,
  extractTextFromPdfClientSide,
  extractTextFromPptx,
  extractTextFromRtf,
  extractTextFromTextFile,
  extractTextFromXlsx,
  fileToBase64,
  getExtension,
  isDocxFile,
  isImageFile,
  isPdfFile,
  isPptxFile,
  isRtfFile,
  isTextFile,
  isXlsxFile,
  isSupportedFileType,
} from '../../claudeDocumentAnalyzer';
import { runTaskStream } from '../../openRouterClient';
import {
  DOCUMENT_ANALYSIS_LADDER,
} from '../../../config/modelTiers.generated';
import { isAgenticWorkflowsAuthorized } from '../../agenticWorkflows';
import { makeRegexTrigger } from '../triggerHelpers';
import { buildModelDisplay } from '../modelDisplay';

import type {
  DocumentManifest,
  DocumentManifestEntry,
  GlobalToolDefinition,
  ManifestDocType,
  ManifestLanguage,
  ManifestLegalArea,
  SharedWorkflowContext,
  StepContext,
  StepResult,
  ToolTheme,
  TokenUsageMetadata,
} from '../types';

/** Ledger-row summary for the proxy's server meter (slice 2, 2026-09-15). */
const MANIFEST_METER_SUMMARY = 'multi-doc manifest';

// ============================================================================
// Constants & Configuration
// ============================================================================

/** Hard cap on how many files one manifest call will process. */
export const MAX_MANIFEST_FILES = 20;

/** Per-file extraction character cap. Beyond this, text is truncated. */
export const MAX_EXTRACT_CHARS = 40_000;

/** Per-file summary prompt char cap — used when building the batched prompt. */
const SUMMARY_PROMPT_CHARS_PER_DOC = 4_000;

/** Per-doc output ceiling to protect turn-context budget. */
export const DOC_SUMMARY_TOKEN_CEILING = 500;

/** Per-call token budget for output from the batched summarization call. */
const SUMMARY_MAX_TOKENS = DOC_SUMMARY_TOKEN_CEILING * 5;

/** If local PDF extraction returns less than this, use vision/OCR fallback. */
const LOCAL_PDF_MIN_CHARS_FOR_MANIFEST = 50;

/** Keep targeted vision fallback bounded for browser/proxy stability and cost. */
const VISION_FALLBACK_MAX_BYTES_PDF = 32 * 1024 * 1024;
const VISION_FALLBACK_MAX_BYTES_IMG = 25 * 1024 * 1024;
const VISION_FALLBACK_MAX_TOKENS = 4_000;

// ============================================================================
// Tool State
// ============================================================================

export interface MultiDocManifestState {
  /** Last manifest produced in this session (mirrored in sharedContext) */
  manifest?: DocumentManifest;
}

// ============================================================================
// Theme
// ============================================================================

const MULTI_DOC_MANIFEST_THEME: ToolTheme = {
  accent: '#0f766e', // teal 700
  accentBg: '#ccfbf1', // teal 100
  border: '#14b8a6', // teal 500
  subtitle: '#475569',
  muted: '#64748b',
};

// ============================================================================
// Helpers: Language detection (simple heuristic)
// ============================================================================

/**
 * Lightweight language detector.
 * Returns 'en' | 'es' | 'other' based on frequency of language-specific
 * stopwords. Good enough for manifest classification — downstream tools
 * that need robust detection (e.g. translate) have their own detectors.
 */
export function detectLanguage(text: string): ManifestLanguage {
  if (!text || text.length < 40) return 'other';
  const lower = text.toLowerCase();
  const sample = lower.slice(0, 4000);

  const enStops = [
    ' the ',
    ' and ',
    ' of ',
    ' to ',
    ' in ',
    ' that ',
    ' is ',
    ' for ',
    ' with ',
    ' this ',
    ' are ',
    ' not ',
  ];
  const esStops = [
    ' el ',
    ' la ',
    ' los ',
    ' las ',
    ' de ',
    ' que ',
    ' en ',
    ' por ',
    ' con ',
    ' para ',
    ' es ',
    ' son ',
    ' una ',
    ' está ',
  ];

  let enHits = 0;
  for (const w of enStops) {
    const parts = sample.split(w);
    enHits += parts.length - 1;
  }
  let esHits = 0;
  for (const w of esStops) {
    const parts = sample.split(w);
    esHits += parts.length - 1;
  }

  if (enHits < 3 && esHits < 3) return 'other';
  if (esHits > enHits * 1.25) return 'es';
  if (enHits > esHits * 1.25) return 'en';
  // Close call — prefer the larger one; ties fall through to 'other'
  if (enHits === esHits) return 'other';
  return enHits > esHits ? 'en' : 'es';
}

// ============================================================================
// Helpers: Doc-type / legal-area classification (heuristic)
// ============================================================================

function classifyDocType(filename: string, text: string, mimeType: string): ManifestDocType {
  const ext = getExtension(filename);
  if (['xlsx', 'xls', 'csv'].includes(ext) || mimeType.includes('spreadsheet')) return 'spreadsheet';
  if (['pptx', 'ppt', 'key'].includes(ext) || mimeType.includes('presentation')) return 'presentation';

  const lower = `${filename}\n${text.slice(0, 2000)}`.toLowerCase();

  if (/intake|dwc[-\s]?1|client\s+questionnaire|new\s+client\s+form/.test(lower)) return 'intake-form';
  if (/medical|doctor|diagnosis|treatment|hospital|clinic|patient|prescription|qme|ame/.test(lower)) {
    return 'medical-record';
  }
  if (/passport|driver'?s?\s?license|id\s?card|birth\s?certificate|social\s?security\s?card|matricula/.test(lower)) {
    return 'identification';
  }
  if (/contract|agreement|nda|non[-\s]?disclosure|employment\s+agreement|lease/.test(lower)) {
    return 'contract';
  }
  if (/court|filing|petition|motion|order|hearing|notice\s+to\s+appear|i-?589|i-?130|i-?140|wcab|pre[-\s]?hearing/.test(lower)) {
    return 'court-filing';
  }
  if (/letter|memo|email|correspondence|re:\s|dear\s+(mr|ms|mrs|dr|sir|madam)/.test(lower)) {
    return 'correspondence';
  }
  return 'other';
}

function classifyLegalArea(filename: string, text: string): ManifestLegalArea {
  const lower = `${filename}\n${text.slice(0, 3000)}`.toLowerCase();
  if (/immigration|asylum|uscis|visa|i-?589|i-?130|i-?140|i-?485|removal|deportation|cbp|ice\s|notice\s+to\s+appear|master\s+calendar|naturalization|green\s?card|citizen/.test(lower)) {
    return 'immigration';
  }
  if (/wcab|workers?\s?comp|dwc[-\s]?1|dwc[-\s]?2|qme|ame|work\s+injury|industrial\s+injury|temporary\s+disability|permanent\s+disability/.test(lower)) {
    return 'workers-comp';
  }
  if (/employment|employee|employer|wage|overtime|wrongful\s+termination|discrimination|harassment|fehha|ferpa|flsa/.test(lower)) {
    return 'employment';
  }
  return 'unknown';
}

// ============================================================================
// Helpers: date / party detection
// ============================================================================

/**
 * Extract an ISO date (YYYY-MM-DD) from the text. Looks for common US
 * date formats; returns the *earliest plausible* occurrence or null.
 */
export function detectDate(text: string): string | null {
  if (!text) return null;
  const sample = text.slice(0, 8000);

  // MM/DD/YYYY or M/D/YYYY
  const usMatch = sample.match(/\b(0?[1-9]|1[0-2])[/-](0?[1-9]|[12]\d|3[01])[/-](19|20)\d{2}\b/);
  if (usMatch) {
    const [m, d, y] = [usMatch[1], usMatch[2], usMatch[0].slice(-4)];
    const mm = m.padStart(2, '0');
    const dd = d.padStart(2, '0');
    return `${y}-${mm}-${dd}`;
  }

  // YYYY-MM-DD (already ISO-ish)
  const isoMatch = sample.match(/\b(19|20)\d{2}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])\b/);
  if (isoMatch) return isoMatch[0];

  // Month DD, YYYY   (e.g. "March 16, 2026")
  const longMatch = sample.match(
    /\b(January|February|March|April|May|June|July|August|September|October|November|December)\s+(0?[1-9]|[12]\d|3[01]),?\s+((?:19|20)\d{2})\b/i
  );
  if (longMatch) {
    const monthIdx =
      [
        'january',
        'february',
        'march',
        'april',
        'may',
        'june',
        'july',
        'august',
        'september',
        'october',
        'november',
        'december',
      ].indexOf(longMatch[1].toLowerCase()) + 1;
    if (monthIdx > 0) {
      const mm = String(monthIdx).padStart(2, '0');
      const dd = String(parseInt(longMatch[2], 10)).padStart(2, '0');
      return `${longMatch[3]}-${mm}-${dd}`;
    }
  }

  return null;
}

/**
 * Very rough party extraction — looks for capitalized pairs of names or
 * "Re:" lines. Only returns high-confidence hits to keep the noise low.
 * Downstream tools should treat this as a hint, not as ground truth.
 */
export function detectParties(text: string): string[] {
  if (!text) return [];
  const sample = text.slice(0, 4000);
  const parties = new Set<string>();

  // "Re: Smith v. Jones" style
  const reMatch = sample.match(/Re:\s+([A-Z][A-Za-z'.-]+(?:\s+[A-Z][A-Za-z'.-]+)*)\s+v\.\s+([A-Z][A-Za-z'.-]+(?:\s+[A-Z][A-Za-z'.-]+)*)/);
  if (reMatch) {
    parties.add(reMatch[1]);
    parties.add(reMatch[2]);
  }

  // "Client: NAME" / "Claimant: NAME" / "Applicant: NAME"
  const labeled = sample.matchAll(/\b(?:Client|Claimant|Applicant|Petitioner|Respondent|Plaintiff|Defendant|Employer|Employee)\s*[:-]\s*([A-Z][A-Za-z'.-]+(?:\s+[A-Z][A-Za-z'.-]+){0,3})/g);
  for (const m of labeled) {
    if (m[1]) parties.add(m[1].trim());
  }

  // Cap to 5 to avoid noise
  return Array.from(parties).slice(0, 5);
}

/**
 * Deterministic fallback summary for entries where the batched LLM
 * summarization did not return a result. Uses the document's own metadata
 * and a preview of its extracted text so EVERY document is covered in the
 * consolidated answer — even when the batch returns partial results or the
 * document had an extraction error.
 */
export function generateFallbackSummary(entry: DocumentManifestEntry): string {
  if (entry.error) {
    return entry.language === 'es'
      ? `Documento no procesado (error de extracción: ${entry.error})`
      : `Unprocessed document (extraction error: ${entry.error})`;
  }

  const isSpanish = entry.language === 'es';
  const parts: string[] = [];

  parts.push(`file: ${entry.filename}`);

  const docTypeLabel = entry.docType !== 'other' ? entry.docType.replace(/-/g, ' ') : '';
  if (docTypeLabel) {
    parts.push(isSpanish ? `tipo: ${docTypeLabel}` : `type: ${docTypeLabel}`);
  }

  if (entry.legalArea !== 'unknown') {
    parts.push(isSpanish ? `área: ${entry.legalArea}` : `area: ${entry.legalArea}`);
  }

  if (entry.detectedDate) {
    parts.push(`date: ${entry.detectedDate}`);
  }

  if (entry.detectedParties.length > 0) {
    const partyList = entry.detectedParties.slice(0, 2).join(', ');
    parts.push(`parties: ${partyList}`);
  }

  if (entry.extractedText) {
    const preview = entry.extractedText.slice(0, 600).replace(/\s+/g, ' ').trim();
    if (preview) {
      parts.push(isSpanish ? `contenido: "${preview}"` : `content: "${preview}"`);
    }
  }

  const prefix = isSpanish ? 'Resumen (fallback):' : 'Summary (fallback):';
  return `${prefix} ${parts.join(' | ')}`;
}

// ============================================================================
// Extraction
// ============================================================================

interface ExtractionOutcome {
  text: string;
  mimeType: string;
  error?: string;
  truncated: boolean;
  extractionMethod?: 'local' | 'vision-fallback';
}

function maybeParseSyntheticPdfText(filename: string, text: string | null): string | null {
  if (!text) return null;
  const trimmed = text.trim();
  if (!trimmed) return null;
  const lowerName = filename.toLowerCase();
  const looksWrapped = /\.(json|csv|html|htm|txt|md)\.pdf$/.test(lowerName);
  const looksStructured =
    /^[\[{]/.test(trimmed) ||
    /^\s*<!doctype\s+html/i.test(trimmed) ||
    /^\s*<html[\s>]/i.test(trimmed) ||
    (trimmed.split('\n').slice(0, 5).some((line) => line.includes(',')) && lowerName.includes('.csv.'));
  if (!looksWrapped && !looksStructured) return text;

  let parsed = trimmed;
  if (lowerName.includes('.html.') || /^\s*<!doctype\s+html/i.test(trimmed) || /^\s*<html[\s>]/i.test(trimmed)) {
    parsed = trimmed
      .replace(/<script[\s\S]*?<\/script>/gi, ' ')
      .replace(/<style[\s\S]*?<\/style>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;|&apos;/g, "'")
      .replace(/\s+/g, ' ')
      .trim();
  }
  return parsed || text;
}

async function extractWithVisionFallback(file: File, mimeType: string, reason: string): Promise<ExtractionOutcome> {
  const isPdf = isPdfFile(file);
  const maxBytes = isPdf ? VISION_FALLBACK_MAX_BYTES_PDF : VISION_FALLBACK_MAX_BYTES_IMG;

  if (file.size > maxBytes) {
    return {
      text: '',
      mimeType,
      error: `${reason}; vision/OCR fallback skipped because the file is over ${Math.round(maxBytes / 1024 / 1024)}MB`,
      truncated: false,
    };
  }

  const encoded = await fileToBase64(file);
  if (!encoded) {
    return { text: '', mimeType, error: `${reason}; vision/OCR encoding failed`, truncated: false };
  }

  const dataUrl = `data:${encoded.mediaType};base64,${encoded.base64}`;
  const prompt = [
    'Extract the readable text and visually important facts from this file for a legal intake manifest.',
    'Return plain text only. Preserve the original language of the document. Do not translate.',
    'If the file is a photo/diagram, describe the visible content, labels, dates, names, and any legally relevant facts.',
    `Filename: ${file.name}`,
  ].join('\n');

  // Build an OpenAI-style multimodal content array for OpenRouter. Gemini 3.1
  // Pro handles both images (via image_url) and PDFs (via file) natively and
  // is far more lenient than Anthropic's image size constraints.
  const userContent: any[] = [];
  if (isPdf) {
    userContent.push({
      type: 'file',
      file: { filename: file.name || 'document.pdf', file_data: dataUrl },
    });
  } else {
    userContent.push({ type: 'image_url', image_url: { url: dataUrl } });
  }
  userContent.push({ type: 'text', text: prompt });

  const visionMessages = [
    {
      role: 'system' as const,
      content: 'You are a precise OCR and visual document extraction engine for a legal intake workflow. Return plain extracted text only.',
    },
    // OpenRouter accepts array content on user messages; the client types
    // it as string, so cast via unknown to bypass the generic signature.
    // Door A's converter passes these OpenRouter-form blocks through.
    { role: 'user' as const, content: userContent as unknown as string },
  ];
  let result;
  try {
    // D14 slice 5c: the route name is the routing input — the server stamps
    // the document-analysis ladder + effort (rung 0 was already the model
    // this call sent). Slice 7 (2026-09-15) retired the deploy-skew
    // fallback; a route failure degrades to the error path below.
    result = await runTaskStream({
      route: 'document-analysis',
      meterSummary: MANIFEST_METER_SUMMARY,
      messages: visionMessages,
      maxTokens: VISION_FALLBACK_MAX_TOKENS,
      temperature: 0.2,
    });
  } catch (err) {
    return {
      text: '',
      mimeType,
      error: `${reason}; vision/OCR fallback failed (${err instanceof Error ? err.message : String(err)}).`.slice(0, 320),
      truncated: false,
    };
  }

  const text = (result?.text || '').trim();
  if (!text) {
    return { text: '', mimeType, error: `${reason}; vision/OCR fallback returned no text`, truncated: false };
  }

  let finalText = text;
  let truncated = false;
  if (finalText.length > MAX_EXTRACT_CHARS) {
    finalText = finalText.slice(0, MAX_EXTRACT_CHARS);
    truncated = true;
  }
  return { text: finalText, mimeType, truncated, extractionMethod: 'vision-fallback' };
}

/**
 * Extract raw text from a single file, reusing the existing client-side
 * helpers in claudeDocumentAnalyzer.ts. Scanned/image-only PDFs and images
 * use targeted vision/OCR fallback so the batched manifest remains complete.
 */
export async function extractForManifest(file: File): Promise<ExtractionOutcome> {
  let mimeType = file.type || '';
  if (!mimeType || mimeType === 'application/octet-stream') {
    const ext = getExtension(file.name);
    const extMap: Record<string, string> = {
      pdf: 'application/pdf',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      txt: 'text/plain',
      md: 'text/markdown',
      csv: 'text/csv',
      json: 'application/json',
      xml: 'application/xml',
      html: 'text/html',
      htm: 'text/html',
      rtf: 'application/rtf',
      png: 'image/png',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      gif: 'image/gif',
      webp: 'image/webp',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      xls: 'application/vnd.ms-excel',
      xlsm: 'application/vnd.ms-excel.sheet.macroEnabled.12',
      ods: 'application/vnd.oasis.opendocument.spreadsheet',
      pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      ppt: 'application/vnd.ms-powerpoint',
      odp: 'application/vnd.oasis.opendocument.presentation',
    };
    mimeType = extMap[ext] || mimeType || 'application/octet-stream';
  }

  if (!isSupportedFileType(file)) {
    return {
      text: '',
      mimeType,
      error: `Unsupported format (${getExtension(file.name) || mimeType})`,
      truncated: false,
    };
  }

  let extracted: string | null = null;
  let extractionMethod: ExtractionOutcome['extractionMethod'] = 'local';
  try {
    if (isPdfFile(file)) {
      extracted = maybeParseSyntheticPdfText(file.name, await extractTextFromPdfClientSide(file));
      if (!extracted || extracted.length < LOCAL_PDF_MIN_CHARS_FOR_MANIFEST) {
        const fallback = await extractWithVisionFallback(
          file,
          mimeType,
          'PDF client-side extraction returned little/no text'
        );
        if (fallback.text) return fallback;
        return fallback;
      }
    } else if (isDocxFile(file)) {
      extracted = await extractTextFromDocx(file);
    } else if (isXlsxFile(file)) {
      extracted = await extractTextFromXlsx(file);
    } else if (isPptxFile(file)) {
      extracted = await extractTextFromPptx(file);
      if (!extracted || extracted.length < LOCAL_PDF_MIN_CHARS_FOR_MANIFEST) {
        return await extractWithVisionFallback(
          file,
          mimeType,
          'Presentation text extraction returned little/no text'
        );
      }
    } else if (isTextFile(file)) {
      extracted = await extractTextFromTextFile(file);
    } else if (isRtfFile(file)) {
      extracted = await extractTextFromRtf(file);
    } else if (isImageFile(file)) {
      const fallback = await extractWithVisionFallback(
        file,
        mimeType,
        'Image file requires vision/OCR extraction'
      );
      if (fallback.text) return fallback;
      return fallback;
    } else {
      return {
        text: '',
        mimeType,
        error: `Extractor not available for this file type (${getExtension(file.name) || mimeType})`,
        truncated: false,
      };
    }
  } catch (err) {
    return {
      text: '',
      mimeType,
      error: err instanceof Error ? err.message : String(err),
      truncated: false,
    };
  }

  if (!extracted || !extracted.trim()) {
    return {
      text: '',
      mimeType,
      error: 'No text extracted',
      truncated: false,
    };
  }

  let text = extracted;
  let truncated = false;
  if (text.length > MAX_EXTRACT_CHARS) {
    text = text.slice(0, MAX_EXTRACT_CHARS);
    truncated = true;
  }

  return { text, mimeType, truncated, extractionMethod };
}

// ============================================================================
// Batched LLM summarization
// ============================================================================

interface SummarizationInput {
  index: number;
  filename: string;
  language: ManifestLanguage;
  docType: ManifestDocType;
  legalArea: ManifestLegalArea;
  textPreview: string;
}

interface SummarizationOutput {
  index: number;
  summary: string;
  tags: string[];
}

function estimateTokens(text: string): number {
  return Math.max(1, Math.ceil((text || '').length / 4));
}

function enforceSummaryCeiling(summary: string): string {
  const normalized = (summary || '').replace(/\s+/g, ' ').trim();
  if (!normalized) return '';
  if (estimateTokens(normalized) <= DOC_SUMMARY_TOKEN_CEILING) return normalized;
  const maxChars = DOC_SUMMARY_TOKEN_CEILING * 4;
  return `${normalized.slice(0, Math.max(0, maxChars - 3)).trim()}...`;
}

function detectInstructionSignalsFromText(text: string): string[] {
  const out: string[] = [];
  const patterns = [
    /process\s+per\s+attached/gi,
    /handle\s+per\s+attached/gi,
    /follow\s+the\s+attached\s+protocol/gi,
    /instruction[s]?\s*[:\-]/gi,
    /cover\s+sheet/gi,
  ];
  for (const pattern of patterns) {
    const match = pattern.exec(text);
    if (match && match[0]) out.push(match[0].trim());
  }
  return Array.from(new Set(out)).slice(0, 5);
}

/**
 * Build one prompt that asks the model to return a JSON array with
 * `{ index, summary, tags[] }` for all N documents in a single call.
 *
 * Language policy (from MEMORY.md): summaries MUST be in the same
 * language as the source document. NEVER default to Spanish for English
 * documents.
 */
function buildBatchSummarizationPrompt(docs: SummarizationInput[]): {
  system: string;
  user: string;
} {
  const system = [
    'You are a document intake classifier for a U.S. law firm.',
    'For each document you receive, produce:',
    '  - a 2–3 sentence summary in the SAME language as the source document',
    '    (English source → English summary, Spanish source → Spanish summary).',
    '  - a small list of 3–8 tags useful for faceted retrieval (e.g.',
    '    "intake", "DWC-1", "client:Juan Perez", "date:2026-03-16",',
    '    "asylum", "I-589", etc).',
    '',
    'Output MUST be a single valid JSON array with no prose, no markdown',
    'fences, no preamble. Each element is:',
    '  { "index": <number>, "summary": <string>, "tags": <string[]> }',
    '',
    'Do NOT translate. Do NOT editorialize. Do NOT give legal advice.',
    'Do NOT include any summary in a language different from the source.',
  ].join('\n');

  const blocks = docs
    .map((d) => {
      const preview = d.textPreview.length > SUMMARY_PROMPT_CHARS_PER_DOC
        ? d.textPreview.slice(0, SUMMARY_PROMPT_CHARS_PER_DOC) + '\n[...truncated...]'
        : d.textPreview;
      return [
        `### Document #${d.index}`,
        `Filename: ${d.filename}`,
        `Detected language: ${d.language}`,
        `Detected doc type: ${d.docType}`,
        `Detected legal area: ${d.legalArea}`,
        'Content:',
        preview,
      ].join('\n');
    })
    .join('\n\n---\n\n');

  const user = [
    `Classify and summarize the following ${docs.length} document(s).`,
    // IMPORTANT: phrase this carefully to avoid the proxy's injection filter,
    // which blocks messages that say things like "return ... system prompt" /
    // "repeat the instructions". Keeping it neutral here.
    'Reply with ONLY a JSON array of objects { index, summary, tags } following the schema above. No prose, no markdown fences, no preamble.',
    '',
    blocks,
  ].join('\n');

  return { system, user };
}

function parseBatchSummaryResponse(raw: string, expectedIndices: number[]): SummarizationOutput[] {
  if (!raw) return [];

  // Strip code fences if the model ignored instructions and added them
  let cleaned = raw.trim();
  if (cleaned.startsWith('```')) {
    cleaned = cleaned.replace(/^```(?:json)?\s*/i, '').replace(/```$/i, '').trim();
  }

  // Find the first `[` and last `]` to be defensive against chatty models
  const first = cleaned.indexOf('[');
  const last = cleaned.lastIndexOf(']');
  if (first === -1 || last === -1 || last <= first) return [];
  const sliced = cleaned.slice(first, last + 1);

  try {
    const parsed = JSON.parse(sliced);
    if (!Array.isArray(parsed)) return [];
    const out: SummarizationOutput[] = [];
    const seen = new Set<number>();
    for (const item of parsed) {
      if (!item || typeof item !== 'object') continue;
      const idx = typeof item.index === 'number' ? item.index : Number(item.index);
      if (!Number.isFinite(idx) || !expectedIndices.includes(idx) || seen.has(idx)) continue;
      seen.add(idx);
      const summary = typeof item.summary === 'string' ? item.summary.trim() : '';
      const tags = Array.isArray(item.tags)
        ? item.tags.filter((t: unknown): t is string => typeof t === 'string' && t.trim().length > 0).slice(0, 12)
        : [];
      out.push({ index: idx, summary, tags });
    }
    return out;
  } catch {
    return [];
  }
}

/**
 * Run the batched summarization call over already-extracted TEXT. Rides the
 * document-analysis route's fast fallback rungs (tier-11 rungs 2+), not the
 * heavy vision primary — same deliberate latency exception as the analyzer's
 * text-only path, but route-DERIVED so an SSOT bump reaches it
 * (2026-08-22; the previous comment named models this code no longer used).
 */
async function attemptBatchSummarization(
  docs: SummarizationInput[],
  rung: number,
  signal?: AbortSignal
): Promise<{ outputs: SummarizationOutput[]; tokenUsage?: TokenUsageMetadata; raw: string; err?: unknown }> {
  const { system, user } = buildBatchSummarizationPrompt(docs);
  const indices = docs.map((d) => d.index);
  const messages = [
    { role: 'system' as const, content: system },
    { role: 'user' as const, content: user },
  ];
  // Generous output cap so the closing `]` of the JSON array is always reached
  // even with 20 documents and small reasoning overhead.
  const maxTokens = Math.min(SUMMARY_MAX_TOKENS + docs.length * 1_200, 65_000);
  try {
    // D14 slice 5c: the route name + rung offset are the routing inputs —
    // bulk summarization deliberately rides the document-analysis route's
    // MID rungs (the latency/cost exception below), and the rung offset
    // expresses that without a model id ever leaving the browser (slice 7,
    // 2026-09-15: the deploy-skew fallback retired; the offset pattern is
    // now also how the analyzer's text-only path rides rung 1).
    const result = await runTaskStream({
      route: 'document-analysis',
      meterSummary: MANIFEST_METER_SUMMARY,
      rung,
      messages,
      maxTokens,
      temperature: 0.2,
      signal,
    });
    const raw = typeof result.text === 'string' ? result.text : '';
    const outputs = parseBatchSummaryResponse(raw, indices);
    return { outputs, tokenUsage: result.tokenUsage, raw };
  } catch (err) {
    return { outputs: [], raw: '', err };
  }
}

/** Max docs per single LLM summarization call. Keeps output under token caps. */
const SUMMARY_CHUNK_SIZE = 5;

function chunkArray<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

async function runBatchedSummarization(
  docs: SummarizationInput[],
  signal?: AbortSignal
): Promise<{ outputs: SummarizationOutput[]; tokenUsage?: TokenUsageMetadata; modelUsed?: string; errorStr?: string }> {
  if (docs.length === 0) return { outputs: [] };

  // Display hints only — the rung offsets below are the routing input; the
  // server stamps the real ladder (slice 7: no model id leaves the browser).
  const models = DOCUMENT_ANALYSIS_LADDER.slice(1, 3);

  const seen = new Map<number, SummarizationOutput>();
  let firstTokenUsage: TokenUsageMetadata | undefined;
  let firstModelUsed: string | undefined;
  let lastErr: unknown = null;
  let lastRaw = '';

  // Process in parallel chunks of N docs each. This avoids the "truncated JSON
  // because the model hit its output cap on 20 docs" class of failures.
  const chunks = chunkArray(docs, SUMMARY_CHUNK_SIZE);
  const chunkResults = await Promise.all(
    chunks.map((chunk) => attemptBatchSummarization(chunk, 1, signal))
  );

  for (const r of chunkResults) {
    for (const o of r.outputs) seen.set(o.index, o);
    if (r.err) lastErr = r.err;
    if (r.raw) lastRaw = r.raw;
    if (r.tokenUsage && !firstTokenUsage) firstTokenUsage = r.tokenUsage;
  }
  if (seen.size > 0) firstModelUsed = models[0];

  // Fill any stragglers: retry at the next rung offset.
  const missing = docs.filter((d) => !seen.has(d.index));
  if (missing.length > 0) {
    const retryChunks = chunkArray(missing, SUMMARY_CHUNK_SIZE);
    const retryResults = await Promise.all(
      retryChunks.map((chunk) => attemptBatchSummarization(chunk, 2, signal))
    );
    for (const r of retryResults) {
      for (const o of r.outputs) seen.set(o.index, o);
      if (r.err) lastErr = r.err;
      if (r.raw) lastRaw = r.raw;
    }
    if (retryResults.some((r) => r.outputs.length > 0) && !firstModelUsed) {
      firstModelUsed = models[1];
    }
  }

  if (seen.size > 0) {
    return {
      outputs: Array.from(seen.values()),
      tokenUsage: firstTokenUsage,
      modelUsed: firstModelUsed,
    };
  }

  const errorStr =
    lastErr instanceof Error
      ? lastErr.message
      : lastErr
        ? String(lastErr)
        : `Unknown error. Raw: ${lastRaw.slice(0, 200)}`;
  console.error('[MultiDocManifest] Batched summarization failed across all models:', errorStr);
  return { outputs: [], errorStr };
}

// ============================================================================
// Session-scoped helpers
// ============================================================================

function makeSessionId(): string {
  const rand = Math.random().toString(36).slice(2, 8);
  const ts = Date.now().toString(36);
  return `mdm-${ts}-${rand}`;
}

function symbolicExtractPath(sessionId: string, index: number): string {
  return `session:${sessionId}/extracted/${index}.txt`;
}

function symbolicManifestPath(sessionId: string): string {
  return `session:${sessionId}/manifest.json`;
}

/**
 * Look up a document in the session's manifest by 1-based index.
 * Returns null if no manifest or index is out of range.
 *
 * Downstream tools (translate, legal-doc-creator, deep-research) call
 * this when the user says "use #N" / "document 3" so they can pull
 * `extractedText` directly instead of re-sending the File.
 */
export function getManifestDocument(
  sharedContext: SharedWorkflowContext,
  index: number
): DocumentManifestEntry | null {
  const manifest =
    sharedContext.documentManifestRef?.current ?? sharedContext.documentManifest ?? null;
  if (!manifest) return null;
  if (!Number.isFinite(index) || index < 1) return null;
  return manifest.entries.find((e) => e.index === index) ?? null;
}

// ============================================================================
// Rendering
// ============================================================================

function fmtInt(n: number): string {
  return n.toLocaleString('en-US');
}

export function renderManifestText(manifest: DocumentManifest): string {
  const lines: string[] = [];
  lines.push(`📄 Processed ${manifest.entries.length} document${manifest.entries.length === 1 ? '' : 's'}`);
  lines.push('');
  for (const e of manifest.entries) {
    const dateStr = e.detectedDate || 'no-date';
    const partyStr = e.detectedParties.length > 0 ? ` · ${e.detectedParties[0]}` : '';
    const truncMark = e.truncated ? ' (truncated)' : '';
    lines.push(
      `${e.index}. ${e.filename} — ${e.language.toUpperCase()} · ${e.docType} · ${e.legalArea} · ${dateStr}${partyStr} · ${fmtInt(e.wordCount)} words${truncMark}`
    );
    if (e.summary) {
      lines.push(`   Summary: ${e.summary}`);
    }
    if (e.instructionSignals && e.instructionSignals.length > 0) {
      lines.push(`   Directives: ${e.instructionSignals.join('; ')}`);
    }
    if (e.tags.length > 0) {
      lines.push(`   Tags: ${e.tags.join(', ')}`);
    }
    if (e.error) {
      lines.push(`   ⚠️ ${e.error}`);
    }
  }
  if (manifest.skipped.length > 0) {
    lines.push('');
    lines.push('Skipped:');
    for (const s of manifest.skipped) {
      lines.push(`  - ${s.filename} — ${s.reason}`);
    }
  }
  lines.push('');
  lines.push(
    'You can now reference any document by number — e.g. "use #15 for the legal doc" or "translate #3".'
  );
  return lines.join('\n');
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function renderManifestHtml(manifest: DocumentManifest): string {
  const items = manifest.entries.map((e) => {
    const dateStr = e.detectedDate || 'no-date';
    const partyStr = e.detectedParties.length > 0 ? ` · ${escapeHtml(e.detectedParties[0])}` : '';
    const truncMark = e.truncated
      ? ' <span style="color:#b45309;font-weight:500;">(truncated)</span>'
      : '';
    const meta = `${e.language.toUpperCase()} · ${e.docType} · ${e.legalArea} · ${dateStr}${partyStr} · ${fmtInt(e.wordCount)} words${truncMark}`;
    const summary = e.summary
      ? `<div style="margin-top:4px;color:#334155;">${escapeHtml(e.summary)}</div>`
      : '';
    const directives = Array.isArray(e.instructionSignals) && e.instructionSignals.length > 0
      ? `<div style="margin-top:4px;color:#92400e;font-size:12px;">Directive cues: ${e.instructionSignals.map(escapeHtml).join('; ')}</div>`
      : '';
    const tags =
      e.tags.length > 0
        ? `<div style="margin-top:4px;color:#64748b;font-size:12px;">Tags: ${e.tags.map(escapeHtml).join(', ')}</div>`
        : '';
    const err = e.error
      ? `<div style="margin-top:4px;color:#b91c1c;font-size:12px;">⚠️ ${escapeHtml(e.error)}</div>`
      : '';
    return `
      <li style="margin-bottom:12px;">
        <div style="font-weight:600;color:#0f172a;">
          ${e.index}. ${escapeHtml(e.filename)}
        </div>
        <div style="color:#475569;font-size:12px;">${meta}</div>
        ${summary}
        ${directives}
        ${tags}
        ${err}
      </li>`;
  });

  const skippedBlock =
    manifest.skipped.length > 0
      ? `
        <div style="margin-top:12px;padding:10px 12px;background:#fef3c7;border-radius:8px;font-size:13px;">
          <div style="font-weight:600;color:#92400e;margin-bottom:4px;">Skipped</div>
          <ul style="margin:0;padding-left:18px;color:#78350f;">
            ${manifest.skipped
              .map((s) => `<li>${escapeHtml(s.filename)} — ${escapeHtml(s.reason)}</li>`)
              .join('')}
          </ul>
        </div>`
      : '';

  return `
    <div style="padding:12px 14px;background:#f0fdfa;border:1px solid #99f6e4;border-radius:12px;font-family:'SF Pro Text',-apple-system,BlinkMacSystemFont,sans-serif;">
      <div style="font-weight:600;color:#0f766e;margin-bottom:8px;">
        📄 Processed ${manifest.entries.length} document${manifest.entries.length === 1 ? '' : 's'}
      </div>
      <ol style="margin:0;padding-left:18px;">
        ${items.join('')}
      </ol>
      ${skippedBlock}
      <div style="margin-top:12px;color:#475569;font-size:13px;">
        You can now reference any document by number — e.g. <em>"use #15 for the legal doc"</em> or <em>"translate #3"</em>.
      </div>
    </div>`;
}

// ============================================================================
// Trigger pattern
// ============================================================================

/**
 * Regex for explicit "read/process/review these" phrases, EN + ES.
 * The orchestrator is the primary router — this pattern is here so the
 * tool can be called directly (e.g. via a button) and so the matchToolTrigger
 * path works for power-users typing the phrase.
 */
// Phase 0 hardening: window quantifiers already small (`{0,3}`-style spans
// elsewhere in the file are inside the verb/noun composer and are NOT
// touched). Routing through `makeRegexTrigger` from triggerHelpers adds
// the in-clause negation guard so phrases like
// "ignore these files, just answer" don't fire the manifest builder.
const TRIGGER_PATTERNS: RegExp[] = [
  /^\s*(read|process|review|summari[sz]e|go through|walk me through)\s+(these|all|both)\s+(files?|docs?|documents?|attachments?)\b/i,
  /\b(read|process|review|summari[sz]e)\s+all\s+(of\s+)?(these|the)\s+(files?|docs?|documents?)\b/i,
  /\b(analizar|lee|procesa|revisa|resume|resúmame)\s+(estos|todos)?\s*(los\s+)?(archivos?|documentos?|adjuntos?)\b/i,
];

const matchMultiDocManifestTrigger = makeRegexTrigger(TRIGGER_PATTERNS);

// ============================================================================
// Core processing function (exposed for orchestrator / direct invocation)
// ============================================================================

export interface BuildManifestParams {
  files: File[];
  signal?: AbortSignal;
  sessionId?: string;
}

export interface BuildManifestResult {
  manifest: DocumentManifest;
  tokenUsage?: TokenUsageMetadata;
  modelUsed?: string;
  summarizationFailed: boolean;
}

/**
 * Build a manifest from a set of files. This is the core, non-UI
 * function — it can be called from the tool step, from a programmatic
 * entry point, or from a test harness.
 */
export async function buildDocumentManifest(
  params: BuildManifestParams
): Promise<BuildManifestResult> {
  const { files, signal } = params;
  const sessionId = params.sessionId || makeSessionId();

  const accepted = files.slice(0, MAX_MANIFEST_FILES);
  const overflow = files.slice(MAX_MANIFEST_FILES);

  const entries: DocumentManifestEntry[] = [];
  const skipped: DocumentManifest['skipped'] = overflow.map((f) => ({
    filename: f.name,
    reason: `Exceeds the ${MAX_MANIFEST_FILES}-file batch limit`,
  }));

  // 1) Extract all files in parallel (client-side, no API calls).
  const extractions = await Promise.all(
    accepted.map(async (file, i) => {
      const index = i + 1;
      try {
        const outcome = await extractForManifest(file);
        return { file, index, outcome };
      } catch (err) {
        return {
          file,
          index,
          outcome: {
            text: '',
            mimeType: file.type || 'application/octet-stream',
            error: err instanceof Error ? err.message : String(err),
            truncated: false,
          } as ExtractionOutcome,
        };
      }
    })
  );

  // 2) Build draft entries + separate successful extractions for summarization.
  const summarizationInputs: SummarizationInput[] = [];
  for (const { file, index, outcome } of extractions) {
    const text = outcome.text;
    const language = text ? detectLanguage(text) : 'other';
    const docType = classifyDocType(file.name, text, outcome.mimeType);
    const legalArea = classifyLegalArea(file.name, text);
    const detectedDate = text ? detectDate(text) : null;
    const detectedParties = text ? detectParties(text) : [];
    const wordCount = text ? text.trim().split(/\s+/).filter(Boolean).length : 0;

    const entry: DocumentManifestEntry = {
      index,
      filename: file.name,
      mimeType: outcome.mimeType,
      sizeBytes: file.size,
      wordCount,
      language,
      docType,
      legalArea,
      detectedDate,
      detectedParties,
      summary: '', // filled in after batched call
      tags: [],
      instructionSignals: detectInstructionSignalsFromText(text || ''),
      extractedTextPath: symbolicExtractPath(sessionId, index),
      extractedText: text,
      truncated: outcome.truncated,
      error: outcome.error,
    };
    entries.push(entry);

    if (text && !outcome.error) {
      summarizationInputs.push({
        index,
        filename: file.name,
        language,
        docType,
        legalArea,
        textPreview: text,
      });
    } else if (outcome.error) {
      // Track as skipped for UI clarity (still keep the entry so the
      // index numbering is stable for the user).
      skipped.push({ filename: file.name, reason: outcome.error });
    }
  }

  // 3) Run ONE batched summarization call for all successfully-extracted docs.
  let tokenUsage: TokenUsageMetadata | undefined;
  let modelUsed: string | undefined;
  let summarizationFailed = false;
  let summarizationErrorDetail = '';

  if (summarizationInputs.length > 0) {
    const { outputs, tokenUsage: usage, modelUsed: model, errorStr: summarizationErrorStr } =
      await runBatchedSummarization(summarizationInputs, signal);
    tokenUsage = usage;
    modelUsed = model;
    const byIndex = new Map<number, SummarizationOutput>(outputs.map((o) => [o.index, o]));
    for (const entry of entries) {
      const out = byIndex.get(entry.index);
      if (out) {
        entry.summary = enforceSummaryCeiling(out.summary);
        entry.tags = out.tags;
      }
    }
    if (outputs.length === 0) {
      summarizationFailed = true;
      if (summarizationErrorStr) {
        summarizationErrorDetail = summarizationErrorStr;
      }
    } else if (outputs.length < summarizationInputs.length) {
      summarizationErrorDetail = `Partial summaries: ${outputs.length}/${summarizationInputs.length} docs returned by ${model || 'fallback'}`;
    }
  }

  // 4) Build default tags for entries the model didn't cover (heuristic fallback).
  for (const entry of entries) {
    if (entry.tags.length === 0 && !entry.error) {
      const heuristic: string[] = [];
      heuristic.push(entry.docType);
      if (entry.legalArea !== 'unknown') heuristic.push(entry.legalArea);
      if (entry.detectedDate) heuristic.push(`date:${entry.detectedDate}`);
      if (entry.detectedParties[0]) heuristic.push(`party:${entry.detectedParties[0]}`);
      entry.tags = heuristic;
    }
    if (!entry.summary) {
      entry.summary = enforceSummaryCeiling(generateFallbackSummary(entry));
    }
  }

  const manifest: DocumentManifest = {
    sessionId,
    createdAt: new Date().toISOString(),
    submittedCount: files.length,
    entries,
    skipped,
    manifestPath: symbolicManifestPath(sessionId),
  };

  return { manifest, tokenUsage, modelUsed, summarizationFailed };
}

// ============================================================================
// Tool Definition
// ============================================================================

export const multiDocManifestTool: GlobalToolDefinition<MultiDocManifestState> = {
  id: 'multi-doc-manifest',
  name: 'Multi-Document Manifest',
  description:
    'Batched intake for 2–20 uploaded files: extracts text, summarizes each in 2–3 sentences, tags docs, and persists a session-scoped manifest so downstream tools can reference docs by index.',

  triggerPattern: matchMultiDocManifestTrigger,

  createInitialState: (): MultiDocManifestState => ({
    manifest: undefined,
  }),

  steps: [
    {
      id: 'build-manifest',
      name: 'Build Document Manifest',
      activeDisplayName: 'processing documents',

      execute: async (
        ctx: StepContext<MultiDocManifestState>,
        rawInput?: unknown
      ): Promise<StepResult> => {
        const input = rawInput as { files?: File[] } | undefined;
        const files =
          (input?.files && input.files.length > 0 ? input.files : undefined) ||
          ctx.triggerMessage.attachments ||
          [];

        if (files.length === 0) {
          ctx.messaging.showSpecialMessage({
            header: { title: 'no documents attached' },
            sections: [
              {
                type: 'text',
                content:
                  'Attach 2 or more files (PDF, DOCX, TXT, CSV, JSON, HTML) and I\'ll build a numbered manifest you can reference later.',
              },
            ],
            theme: 'blue',
          });
          return { status: 'completed', nextStep: null };
        }

        if (files.length > MAX_MANIFEST_FILES) {
          ctx.messaging.showWarning(
            'Too many files',
            `The Multi-Document Processor handles up to ${MAX_MANIFEST_FILES} files per batch — I'll process the first ${MAX_MANIFEST_FILES} and list the rest as skipped.`
          );
        }

        const stream = ctx.messaging.showPending('processing documents');
        ctx.sharedContext.setActiveStream?.(stream);

        let streamFinalized = false;
        try {
          const { manifest, tokenUsage, modelUsed, summarizationFailed } =
            await buildDocumentManifest({
              files,
              signal: ctx.abortSignal,
            });

          // Persist on session state (both state and ref, mirroring existing
          // sharedContext conventions used by documentContent / lastResearchReport).
          ctx.sharedContext.setDocumentManifest?.(manifest);
          if (ctx.sharedContext.documentManifestRef) {
            ctx.sharedContext.documentManifestRef.current = manifest;
          }

          // Update internal tool state (for re-entry / cleanup)
          ctx.setState({ manifest });

          // Record transaction (cost tracking) for the batched summarization.
          // modelOverride goes through the consolidated codename builder —
          // previously the raw fallback-ladder id ('~openai/gpt-latest' /
          // '~google/gemini-pro-latest') was recorded verbatim into the
          // wallet ledger display string.
          if (tokenUsage) {
            ctx.recordTransaction({
              tokenUsage: { ...tokenUsage, summary: 'multi-doc manifest summarization' },
              source: 'globalTools',
              modelOverride: buildModelDisplay(
                { ...tokenUsage, model: modelUsed || tokenUsage.model },
                'Manifest',
                'Prism (Manifest)',
              ),
              summary: `manifest (${manifest.entries.length} docs)`,
            });
          }

          const text = renderManifestText(manifest);
          const html = renderManifestHtml(manifest);

          if (summarizationFailed) {
            // Soft-failure: still deliver the manifest (with heuristic
            // summary fallbacks) so the user gets the numbered overview.
            ctx.messaging.showWarning(
              'Summaries unavailable',
              'All fallback summarization models failed. The numbered manifest is still available — summaries will show a fallback note.'
            );
          }

          stream.finalize({
            text,
            html,
            sourceTag: 'multi_doc_manifest_result',
            tokenUsage,
          });
          streamFinalized = true;

          return {
            status: 'completed',
            output: { manifest },
            carryForward: { manifest },
          };
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error);
          stream.finalize({
            payload: {
              kind: 'multi_doc_manifest.update',
              toolId: 'multi-doc-manifest',
              severity: 'error',
              data: { errorMessage: message, phase: 'manifest_processing' },
              summary: `multi-document manifest failed: ${message}`,
            },
            sourceTag: 'multi_doc_manifest_error',
          });
          streamFinalized = true;
          return {
            status: 'failed',
            error: error instanceof Error ? error : new Error(message),
            userFacingErrorShown: true,
          };
        } finally {
          if (!streamFinalized) {
            try {
              stream.finalize({
                payload: {
                  kind: 'multi_doc_manifest.update',
                  toolId: 'multi-doc-manifest',
                  severity: 'error',
                  data: { errorMessage: 'multi-document processing ended unexpectedly', phase: 'emergency_fallback' },
                  summary: 'multi-document manifest: emergency fallback fired',
                },
                sourceTag: 'multi_doc_manifest_emergency',
              });
            } catch {
              // swallow — emergency path only
            }
          }
          ctx.sharedContext.setActiveStream?.(null);
        }
      },

      cleanup: async () => {
        // No in-flight flags to reset — state.manifest is terminal output.
      },
    },
  ],

  isAuthorized: isAgenticWorkflowsAuthorized,

  theme: MULTI_DOC_MANIFEST_THEME,

  buttonIds: {
    multiDocManifest: 'global-tool-multi-doc-manifest-btn',
  },

  canTriggerFromButton: true,
};
